import { Link, useParams } from "react-router-dom";
import { useSeo, SITE_ORIGIN, SITE_NAME } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Reveal from "@/components/feature/Reveal";
import PostCard from "@/components/feature/PostCard";
import { posts } from "@/mocks/posts";
import { zones } from "@/mocks/zones";

function formatReads(count: number) {
  return count >= 10000 ? `${(count / 10000).toFixed(1)}万` : `${count}`;
}

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const post = posts.find((item) => item.id === id);
  const zone = zones.find((item) => item.id === post?.zoneId);

  const sameZone = posts.filter((item) => item.zoneId === post?.zoneId && item.id !== post?.id);
  const others = posts.filter((item) => item.id !== post?.id && item.zoneId !== post?.zoneId);
  const related = [...sameZone, ...others].slice(0, 3);

  const wordCount = post ? post.paragraphs.join("").length : 0;
  const readMinutes = Math.max(1, Math.round(wordCount / 350));

  useSeo({
    title: post ? `${post.title} - 爆料91${zone ? zone.name : ""}专区` : "内容未找到 - 爆料91",
    description: post ? post.excerpt : "该内容不存在或已下线，可返回爆料91专区继续浏览其他合集。",
    keywords: post ? `爆料91,${zone ? zone.name : "专区"},${post.tags.join(",")}` : "爆料91专区",
    path: `/post/${id ?? ""}`,
    type: "article",
    image: post?.cover,
    jsonLd: post
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: post.cover,
            datePublished: post.date,
            dateModified: post.date,
            inLanguage: "zh-CN",
            keywords: post.tags.join(","),
            author: { "@type": "Organization", name: post.author },
            publisher: { "@type": "Organization", name: SITE_NAME },
            mainEntityOfPage: `${SITE_ORIGIN}/post/${post.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
              { "@type": "ListItem", position: 2, name: "专区总览", item: `${SITE_ORIGIN}/zones` },
              {
                "@type": "ListItem",
                position: 3,
                name: zone ? zone.name : "专区",
                item: zone ? `${SITE_ORIGIN}/zone/${zone.id}` : `${SITE_ORIGIN}/zones`,
              },
              { "@type": "ListItem", position: 4, name: post.title, item: `${SITE_ORIGIN}/post/${post.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!post) {
    return (
      <section className="w-full px-4 md:px-8 py-20 md:py-28">
        <div className="max-w-xl mx-auto text-center rounded-lg border border-background-200 bg-background-100 py-16 px-6">
          <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
          <h1 className="mt-5 font-heading font-bold text-2xl text-foreground-950">没有找到这条内容</h1>
          <p className="mt-3 text-sm text-foreground-600">
            内容编号可能有误，或该条目已从合集里移除。回到专区总览可以继续浏览其他合集。
          </p>
          <Link
            to="/zones"
            className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors"
          >
            <i className="ri-layout-grid-fill"></i>
            返回专区总览
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 pt-6 pb-10 md:pb-12">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "专区总览", to: "/zones" },
              { label: zone ? zone.name : "专区", to: zone ? `/zone/${zone.id}` : "/zones" },
              { label: "正文" },
            ]}
          />
          <div className="mt-6 max-w-3xl">
            {zone && (
              <Link
                to={`/zone/${zone.id}`}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-primary-600 text-background-50 cursor-pointer hover:bg-primary-700 transition-colors"
              >
                <i className="ri-bookmark-3-fill"></i>
                {zone.name}
              </Link>
            )}
            <h1 className="mt-4 font-heading font-black text-2xl md:text-4xl leading-[1.25] text-foreground-950 text-balance">
              {post.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {post.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {formatReads(post.readCount)} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-timer-line"></i>
                约 {readMinutes} 分钟
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <article className="lg:col-span-8">
              <figure className="w-full aspect-[16/9] overflow-hidden rounded-lg bg-background-200">
                <img
                  src={post.cover}
                  alt={`${post.title} 配图`}
                  title={`${post.title} - 爆料91 ${zone ? zone.name : ""}专区`}
                  className="w-full h-full object-cover object-top"
                />
              </figure>

              <p className="mt-8 text-base md:text-lg leading-relaxed text-foreground-800 font-medium border-l-2 border-primary-500 pl-4">
                {post.excerpt}
              </p>

              <div className="mt-8 flex flex-col gap-5">
                {post.paragraphs.map((paragraph, index) => (
                  <p
                    key={`${post.id}-p-${index}`}
                    className="text-[15px] md:text-base leading-[1.9] text-foreground-700"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <Link
                    key={tag}
                    to="/zones"
                    className="text-xs px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-background-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-foreground-500">
                  本文由 {post.author} 整理 · 所涉作品与人物均为设定内容
                </p>
                {zone && (
                  <Link
                    to={`/zone/${zone.id}`}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-foreground-300 text-sm text-foreground-900 whitespace-nowrap cursor-pointer hover:border-foreground-900 transition-colors"
                  >
                    <i className="ri-arrow-left-line"></i>
                    回到 {zone.name}
                  </Link>
                )}
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 flex flex-col gap-6">
                {zone && (
                  <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <h2 className="font-heading font-bold text-lg text-foreground-950">所属专区</h2>
                    <Link
                      to={`/zone/${zone.id}`}
                      className="group mt-3 flex items-center gap-3 cursor-pointer"
                    >
                      <span className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={zone.cover}
                          alt={`${zone.name}专区封面`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="flex flex-col min-w-0">
                        <span className="font-medium text-foreground-900 group-hover:text-primary-700 transition-colors">
                          {zone.name}
                        </span>
                        <span className="mt-1 text-xs text-foreground-500">{zone.subtitle}</span>
                      </span>
                    </Link>
                    <Link
                      to={`/zone/${zone.id}`}
                      className="mt-4 inline-flex items-center gap-1 text-sm text-primary-700 font-medium cursor-pointer hover:gap-2 transition-all"
                    >
                      查看该专区全部合集
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                )}

                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading font-bold text-lg text-foreground-950">相关推荐</h2>
                  <div className="mt-4 flex flex-col gap-4">
                    {related.map((item) => (
                      <Link
                        key={item.id}
                        to={`/post/${item.id}`}
                        className="group flex gap-3 cursor-pointer"
                      >
                        <span className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                          <img
                            src={item.cover}
                            alt={`${item.title} 配图`}
                            title={`${item.title} - 爆料91专区合集`}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </span>
                        <span className="flex flex-col min-w-0">
                          <span className="text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                            {item.title}
                          </span>
                          <span className="mt-1 text-xs text-foreground-500">{item.date}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                  <h2 className="font-heading font-bold text-lg text-foreground-950">找更多内容</h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                    这条内容只是合集里的一期。进入专区总览，可以看到同类方向下的全部整理。
                  </p>
                  <Link
                    to="/zones"
                    className="mt-4 inline-flex items-center justify-center gap-2 h-10 px-4 w-full rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors"
                  >
                    <i className="ri-compasses-2-fill"></i>
                    浏览全部专区
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="w-full bg-background-100 border-t border-background-200">
          <div className="w-full px-4 md:px-8 py-12 md:py-16">
            <h2 className="font-heading font-bold text-2xl text-foreground-950">接着看这几条</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((item, index) => (
                <Reveal key={item.id} delay={index * 60}>
                  <PostCard post={item} variant="cover" />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}